import { useState, useEffect, useRef } from "react";
import AvatarDetails from "@/components/AvatarDetails";
import PersonAutocomplete from "./PersonAutocomplete";
import classNames from "classnames";

interface InlinePersonAutocompleteCellProps {
  name: string;
  pgaId: string;
  profilePhoto: string | null;
  email?: string | null;
  onEdit: (pgaId: string) => void;
}

export default function InlinePersonAutocompleteCell({
  name,
  pgaId,
  profilePhoto,
  email,
  onEdit
}: InlinePersonAutocompleteCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [personSearch, setPersonSearch] = useState(name && name !== "--" ? name : "");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing) {
      setPersonSearch(name && name !== "--" ? name : "");
    }
  }, [isEditing, name]);

  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsEditing(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing]);

  const handleSelect = (person: { pgaId?: string | null }) => {
    const newPgaId = person.pgaId || "";
    if (newPgaId && newPgaId !== pgaId) {
      onEdit(newPgaId);
    }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div ref={containerRef} className="min-w-[200px]" onClick={(e) => e.stopPropagation()}>
        <PersonAutocomplete
          label=""
          query={personSearch}
          onChangeText={setPersonSearch}
          onSelect={handleSelect}
          autofocus
          skip={!isEditing}
        />
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={classNames(
        "cursor-pointer rounded-md px-2 py-1.5 -ml-2 -my-1",
        "hover:bg-sys-brand-surface-container-high",
        "border-2 border-transparent focus:border-sys-brand-primary focus:outline-none"
      )}
      onClick={(e) => {
        e.stopPropagation();
        setIsEditing(true);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsEditing(true);
        }
      }}
    >
      <AvatarDetails picture={profilePhoto ?? undefined} title={name || "--"} text={email ?? undefined} />
    </div>
  );
}
